/**
 * CaseFlow+AI - Language Provider
 * Provides language context and persistence for i18n
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { Language } from './index';
import { t as translate, getTranslation } from './index';

interface LanguageContextValue {
  language: Language;
  setLanguage: (language: Language) => void;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    // Check localStorage first
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('caseflow-language');
      if (stored === 'sv' || stored === 'en') {
        return stored;
      }
    }
    // Default to Swedish
    return 'sv';
  });

  useEffect(() => {
    // Store preference
    localStorage.setItem('caseflow-language', language);
    
    // Set HTML lang attribute for accessibility
    document.documentElement.lang = language;
  }, [language]);

  const setLanguage = useCallback((newLanguage: Language) => {
    setLanguageState(newLanguage);
  }, []);

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextValue {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

/**
 * Translation hook that provides the t() function bound to the current language
 * Use this in components to get translated strings
 * 
 * @example
 * const { t, language } = useTranslation();
 * return <span>{t('common.save')}</span>;
 */
export function useTranslation() {
  const { language, setLanguage } = useLanguage();
  
  const t = (key: string): string => {
    return translate(language, key);
  };
  
  return {
    t,
    language,
    setLanguage,
    /**
     * Get the full translation object for the current language
     */
    translations: getTranslation(language),
  };
}

export default LanguageProvider;
